import leven from "js-levenshtein";
import { compareCaseInsensitive } from "../../../../../utils/string-utils";
import moment from "moment";

const MAX_ITEMS = 20;
const ISBN_LENGTH_DELTA = 0;
const START_WORD = 0.2;
const START_TEXT = 0.5;

function getScoreThreshold(term) {
  if (term.length <= 5) {
    return 1;
  }
  return 2;
}

function stringMatches(actual, desired) {
  const cleanedActual = actual ? actual.toLowerCase() : "";
  return cleanedActual.indexOf(desired) > -1;
}

export function typeMatches(result, type) {
  const { vbkType, layout } = result;

  switch (type) {
    case "pdf":
    case "pd":
      return stringMatches(vbkType, "pbk");
    case "flow":
      return stringMatches(layout, "reflowable");
    default:
      return stringMatches(vbkType, type) || stringMatches(layout, type);
  }
}

/**
 * If we don't need fuzzy matching, this is a more efficient choice
 */
function computeFixedDistance(content, searchFor) {
  const index = content.indexOf(searchFor);
  if (index === -1) {
    return Number.MAX_VALUE;
  }

  if (index === 0) {
    return -START_TEXT;
  } else if (content.charAt(index - 1) === " ") {
    return -START_WORD;
  }

  return 0;
}

function computeFuzzyDistance(content, searchFor) {
  if (searchFor.length > content.length) {
    return Number.MAX_VALUE;
  }

  // Really short ones aren't worth making fuzzy
  if (searchFor.length <= 2) {
    return computeFixedDistance(content, searchFor);
  }

  let score = Number.MAX_VALUE;
  for (let i = 0; i < content.length - searchFor.length + 1; i++) {
    let adjustedScore = leven(content.substr(i, searchFor.length), searchFor);
    // Beginning of text and beginning of word matches are "better" than mid-word matches
    if (i === 0) {
      adjustedScore -= START_TEXT;
    } else if (content.charAt(i - 1) === " ") {
      adjustedScore -= START_WORD;
    }

    score = Math.min(score, adjustedScore);
    if (score < 0) {
      // Can't get any lower, so stop looking
      break;
    }
  }

  return score;
}

function compareResults(a, b) {
  if (a.score === b.score) {
    if (!a.viewed && !b.viewed) {
      return compareCaseInsensitive(a.book.title, b.book.title);
    }

    if (a.viewed && b.viewed) {
      return b.viewed - a.viewed;
    }
    return a.viewed ? -1 : 1;
  }
  return a.score - b.score;
}

function getDataObj(book, score, now) {
  // We are going to sort books opened in the last 30 days
  // by date before books alphabetically, but let's only compute
  // this data once per book, not for every comparison
  let viewed = null;
  if (book.viewedAt) {
    const viewedMoment = moment(book.viewedAt);
    if (viewedMoment.add(30, "days").isAfter(now)) {
      viewed = viewedMoment.valueOf();
    }
  }

  return {
    book,
    score,
    viewed
  };
}

/**
 * Why is this not a selector, you ask? There can be performance concerns with large
 * data sets, and being it manually invoked gives us more flexibility to defer
 * computation in various ways while also getting rid of any overhead involved
 * with using redux
 */
export function quickSearchLicensedContent(books, term = "", type = "") {
  if (term === "" && type === "") {
    return [];
  }

  term = term.toLowerCase();
  type = type.toLowerCase();

  const greatResults = [];
  const okResults = [];
  const scoreThreshold = getScoreThreshold(term);
  const now = moment();

  books.forEach(book => {
    let score = 0;
    if (type === "" || typeMatches(book, type)) {
      if (term !== "") {
        const titleScore = computeFuzzyDistance(book.title.toLowerCase(), term);
        const bookAuthor = book.author ? book.author.toLowerCase() : "";
        const authorScore = computeFixedDistance(bookAuthor, term);
        score = Math.min(titleScore, authorScore);

        // Move highly-likely isbn matches that are not great text matches to the very front
        if (
          score > 0 &&
          book.isbn.length - term.length <= ISBN_LENGTH_DELTA &&
          book.isbn.substr(0, term.length).toLowerCase() === term
        ) {
          score = Math.min(score, -1);
        }
      }

      if (score <= scoreThreshold) {
        const data = getDataObj(book, score, now);
        if (score < 0) {
          greatResults.push(data);
        } else {
          okResults.push(data);
        }
      }
    }
  });

  // Reduce sort overhead by grouping results by score
  const results =
    greatResults.length >= 3 ? greatResults : greatResults.concat(okResults);

  // We are limiting the total items here to force a limit on the map
  return results
    .sort(compareResults)
    .slice(0, MAX_ITEMS)
    .map(r => r.book);
}
